import ObjectUtils from '@spothero/utils/object';
import {cpServiceGet} from 'api';
import {sendSentryException} from 'utils/error-handling';
import dayjs from 'utils/dayjs';

/**
 * Formats the request parameters for getting star ratings based on a selected period.
 *
 * @param {Object<TStarRatingPeriod.period>} selectedPeriod - The period for which the star ratings are requested.
 * @param {string} selectedPeriod.unit - The unit of time (e.g., 'day', 'month').
 * @param {number|string} selectedPeriod.value - The value representing the amount of the unit.
 * @returns {Object} An object containing the formatted date range and unit.
 * @returns {string} return.dateTo - The current date in 'YYYY-MM-DD' format.
 * @returns {string} return.dateFrom - The start date based on the selected period in 'YYYY-MM-DD' format.
 * @returns {string} return.unit - The unit of time used for the selected period.
 *
 * @memberof module:api/facilities/dto
 */
const formatGetStarRatingsRequest = ({selectedPeriod}) => {
    const dateTo = dayjs().format('YYYY-MM-DD');
    // we subtract 1 from the value to ensure that we're including the current month/day/year in the lookback period
    const dateFrom = dayjs()
        .subtract(parseInt(selectedPeriod.value - 1, 10), selectedPeriod.unit)
        .format('YYYY-MM-DD');

    return {
        dateTo,
        dateFrom,
        unit: selectedPeriod.unit,
    };
};

/**
 * Formats the response from the star ratings API.
 *
 * @function
 * @param {Object} response - The response object from the star ratings API.
 * @returns {Object} The formatted response object.
 *
 * @memberof module:api/facilities/dto
 */
const formatGetStarRatingsRepsonse = (response) => {
    const formattedResponse = ObjectUtils.camelizeKeys(response);

    return formattedResponse;
};

/**
 * Gets a facility's aggregated star ratings within a date range
 *
 * @param {number} facilityId
 * @param {Object<TStarRatingPeriod.period>} selectedPeriod - The period for which the star ratings are requested.
 * @returns {Promise<Object>}
 * @throws {Error}
 *
 * @see '/control-panel-service/v1/facilities/{facilityId}/star-ratings'
 *
 */
const getStarRatings = async (facilityId, selectedPeriod) => {
    try {
        const {dateTo, dateFrom, unit} = formatGetStarRatingsRequest({
            selectedPeriod,
        });

        const data = await cpServiceGet(
            `facilities/${facilityId}/star-ratings/?dateFrom=${dateFrom}&dateTo=${dateTo}&unit=${unit}`
        );

        const formattedResponse = formatGetStarRatingsRepsonse(data.data.data);

        return formattedResponse;
    } catch (err) {
        sendSentryException(err);

        throw new Error(err.message);
    }
};

export default getStarRatings;
